import { Location } from '@angular/common';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
    LsqApiServiceListSource,
    LsqListSource,
    SortDirectionFinancing
} from '@lsq-frontend/angular-shared/api-service';
import {
    ClaimService,
    Color,
    DateFormat,
    FeatureFlagService,
    ISelectOption,
    Icons,
    LifecycleAware,
    LsqDatePipe,
    ResponsiveService,
    Table,
    TableHelper,
    WindowReferenceService,
    forLifeOf,
    getDynamicRouterLink,
    getRouterLinkById
} from '@lsq-frontend/common-ui';
import {
    FastTrackRouteQueryParams,
    FeatureFlags
} from '@lsq-frontend/fasttrack-shared/app-constants';
import { GetAccountPaymentExportService } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/payments-list/get-account-payment-export';
import { BuyerPaymentsGroupedDetailRequest } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer/buyer-payments-list/get-grouped-payment';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, first, map, skip, switchMap } from 'rxjs/operators';
import { parseYearMonthDayStringToDate } from '../../../../../helpers/date.helpers';
import {
    ACCOUNT_SUPPLIER_DETAILS,
    BUYER_ACCOUNT_INVOICE_DETAIL,
    BUYER_SUPPLIER_DETAILS,
    SUPPLY_CHAIN_DIRECT_ACCOUNT_PARENT_GROUPED_INVOICE_DETAIL,
    SUPPLY_CHAIN_DIRECT_BUYER_PARENT_GROUPED_INVOICE_DETAIL,
    atlas,
    supplyChainDirectBuyerAtlas
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    AccountPaymentsGroupedDetailRequest,
    GetAccountGroupedPaymentService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/payments-list/get-account-grouped-payment';
import {
    AccountPaymentsDetailAssociatedSuppliersRequest,
    AccountPaymentsDetailAssociatedSuppliersResponse,
    ListSuppliersAssociatedWithAccountPaymentService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/payments-list/list-suppliers-associated-with-account-payment';
import {
    BuyerPaymentsDetailRequest,
    BuyerPaymentsDetailResponse
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer/buyer-payments-list/get-payment';
import {
    BuyerPaymentsDetailAssociatedSuppliersRequest,
    BuyerPaymentsDetailAssociatedSuppliersResponse
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer/buyer-payments-list/list-suppliers-associated-with-payment';
import { BuyerPaymentsDetailItem } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/buyer-payments-detail-item';
import { BuyerPaymentsGroupedDetailItem } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/buyer-payments-grouped-detail-item';
import { BuyerPaymentsSummaryItem } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/buyer-payments-summary-item';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    BuyerPaymentsDetailItem2,
    GetAccountPaymentService,
    GetPaymentService,
    ListSuppliersAssociatedWithPaymentService,
    GetGroupedPaymentService
} from '@lsq-frontend/remote-services/financing-graphql';
import { GetBuyerPaymentDetailsExportService } from '@lsq-frontend/remote-services/report-center';
import { uniqBy } from 'lodash';
import { TAKE_ALL } from '../../../constants';

@LifecycleAware()
@Component({
    selector: 'supply-chain-direct-buyer-payment-detail',
    templateUrl: './payment-detail.component.html',
    styleUrls: ['./payment-detail.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AccountPaymentDetailComponent implements OnInit {
    public readonly color = Color;
    public readonly icons = Icons;
    public readonly nullValue = Table.NullAmount;

    public reportName$: Observable<string>;

    public readonly earlyPaymentType = 'EARLY_PAYMENT';
    public readonly invoiceTermType = 'INVOICE_TERM';
    public readonly payLaterPaymentType = 'PAY_LATER_PAYMENT';
    public paymentPayer$: Observable<string>;
    public payer = 'UNKOWN_PAYER';
    public payerLSQ = 'LSQ';
    public payerBuyer = 'BUYER';

    public paymentTypeRequestString:
        | 'EARLY_PAYMENT'
        | 'INVOICE_TERM'
        | 'EARLY_PAY_REMITTANCE'
        | 'UNKNOWN_PAYMENT_TYPE';

    public readonly expandedGroupInvoiceKeys = new Set<string>();

    public readonly paymentsHeader = this._i18n('Payment Date');
    public readonly referenceNumberHeader = this._i18n('Reference Number');
    public readonly payerHeader = this._i18n('Payer');
    public readonly recipientHeader = this._i18n('Recipient');
    public readonly typeHeader = this._i18n('Type');
    public readonly invoicesHeader = this._i18n('Invoices');
    public readonly paymentAmountHeader = this._i18n('Payment Amount');
    public readonly paymentTotalHeader = this._i18n('Payment Total');
    public readonly supplierHeader = this._i18n('Supplier');
    public readonly invoiceDateHeader = this._i18n('Invoice Date');
    public readonly daysPaidHeader = this._i18n('Days Paid early');
    public readonly invoiceAmountHeader = this._i18n('Invoice Amount');
    public readonly invoiceTotalHeader = this._i18n('Invoice Total');
    public readonly earlyPaymentTitle = this._i18n('Payments - Early Payment');
    public readonly invoiceTermsTitle = this._i18n('Payments - Invoice Terms');
    public readonly payLaterTitle = this._i18n('Payments'); //Temporary until route is created

    public readonly lsqPaymentAmountHeader = this._i18n('LSQ Payment Amount');
    public readonly supplierPaymentAmountHeader = this._i18n('Supplier Payment Amount');
    public readonly payLaterFeeHeader = this._i18n('Fees Charged');
    public readonly paidFeesAmountHeader = this._i18n('Fees Paid');
    public readonly earlyPaymentDateHeader = this._i18n('Early Payment Date');
    public readonly payLaterDateHeader = this._i18n('FlexTerm Date');

    public getRowClassNameBound = this._getRowClassName.bind(this);
    public goToGroupDetailBound = this.goToGroupDetail.bind(this);
    public isInvoiceClickableBound = this.isInvoiceClickable.bind(this);
    public readonly allSuppliers = this._i18n('All Suppliers');
    public readonly selectSupplier = this._i18n('Select Supplier');
    public isBuyerAccountPaymentsPageOn$ = this._featureFlagService.isOn(
        FeatureFlags.BuyerAccountPaymentsPage
    );

    public supplierFilterOptions: ISelectOption[] = [];

    public readonly referenceNumberTruncateAfter$ = this.windowReferenceService.resizeStream.pipe(
        map((dimensions) => {
            if (dimensions.width > 1525) {
                return 24;
            }

            if (dimensions.width > 1100) {
                return 14;
            }
            return 10;
        })
    );

    public readonly supplierTruncateAfter$ = this.windowReferenceService.resizeStream.pipe(
        map((dimensions) => {
            if (dimensions.width > 1525) {
                return 16;
            }

            if (dimensions.width > 1100) {
                return 14;
            }
            return 10;
        })
    );

    public isSummaryLoaded = false;
    public isFinancingAccountPortalUser = false;
    public summaryDataSource: LsqListSource<BuyerPaymentsSummaryItem>;
    public paymentDetailFiltersFormGroup: FormGroup;
    public dataSource: LsqApiServiceListSource<
        BuyerPaymentsDetailResponse,
        BuyerPaymentsDetailRequest,
        BuyerPaymentsDetailItem
    >;

    public isPaymentOnTerms = false;
    public paymentKey = '';

    public readonly buyerPaymentsPaymentTypeDisplayEnum = {
        EARLY_PAYMENT: this._i18n('Early Payment'),
        EARLY_PAY_REMITTANCE: this._i18n('Early Payment'),
        INVOICE_TERM: this._i18n('Paid at Terms'),
        PAY_LATER_PAYMENT: this._i18n('FlexTerm'),
        UNKNOWN_PAYMENT_TYPE: ''
    };

    public readonly buyerPaymentsRecipientDisplayEnum = {
        LSQ: this._i18n('LSQ'),
        SUPPLIERS: this._i18n('Supplier(s)'),
        UNKNOWN_RECIPIENT: ''
    };

    public payerTypeDisplay = {
        LSQ: 'LSQ',
        DEPOSIT_ACCOUNT: '', // Set to blank until we can get the legal name.
        BUYER: '' // Set to blank until we can get the legal name.
    };

    public payLater$ = this._featureFlagService.isOn(FeatureFlags.PayLater);

    constructor(
        private _i18n: I18n,
        public getPaymentService: GetPaymentService,
        public getAccountPaymentService: GetAccountPaymentService,
        public listSuppliersAssociatedWithPaymentService: ListSuppliersAssociatedWithPaymentService,
        public listSuppliersAssociatedWithAccountPaymentService: ListSuppliersAssociatedWithAccountPaymentService,
        private _activatedRoute: ActivatedRoute,
        private _location: Location,
        private _featureFlagService: FeatureFlagService,
        private _router: Router,
        private router: Router,
        public windowReferenceService: WindowReferenceService,
        public responsiveService: ResponsiveService,
        public getGroupedPaymentService: GetGroupedPaymentService,
        public getAccountPaymentExportService: GetAccountPaymentExportService,
        public getAccountGroupedPaymentService: GetAccountGroupedPaymentService,
        public getPaymentExportService: GetBuyerPaymentDetailsExportService,
        public lsqDatePipe: LsqDatePipe,
        private _claimService: ClaimService
    ) {
        this.isFinancingAccountPortalUser = this._claimService.isOnSync(
            DashboardClaim.FinancingAccountPortal
        );
    }

    public ngOnInit(): void {
        this.paymentDetailFiltersFormGroup = new FormGroup({
            search: new FormControl(),
            supplierFilter: new FormControl([])
        });

        if (
            this.router.url.includes(
                supplyChainDirectBuyerAtlas
                    .SUPPLY_CHAIN_DIRECT_BUYER_EARLY_PAYMENT_REMITTANCE_DETAILS.path
            )
        ) {
            this.paymentTypeRequestString = 'EARLY_PAY_REMITTANCE';
        } else if (
            this.router.url.includes(
                supplyChainDirectBuyerAtlas.SUPPLY_CHAIN_DIRECT_BUYER_EARLY_PAYMENT_DETAILS.path
            )
        ) {
            this.paymentTypeRequestString = 'EARLY_PAYMENT';
        } else if (
            this.router.url.includes(
                supplyChainDirectBuyerAtlas.SUPPLY_CHAIN_DIRECT_BUYER_INVOICE_TERMS_PAYMENT_DETAILS
                    .path
            )
        ) {
            this.paymentTypeRequestString = 'INVOICE_TERM';
            this.isPaymentOnTerms = true;
        } else {
            this.paymentTypeRequestString = 'UNKNOWN_PAYMENT_TYPE';
        }

        this._activatedRoute.params.pipe(forLifeOf(this)).subscribe(async (params) => {
            this.paymentKey = params.earlyPaymentKey;

            let suppliersResponse:
                | BuyerPaymentsDetailAssociatedSuppliersResponse
                | AccountPaymentsDetailAssociatedSuppliersResponse =
                new BuyerPaymentsDetailAssociatedSuppliersResponse();
            if (!this._featureFlagService.isOnSync(FeatureFlags.BuyerAccountPaymentsPage)) {
                suppliersResponse =
                    await this.listSuppliersAssociatedWithPaymentService.executeOnce(
                        new BuyerPaymentsDetailAssociatedSuppliersRequest({
                            batchKey: this.paymentKey,
                            buyerPaymentsListPaymentTypeEnum: this.paymentTypeRequestString,
                            pagination: {
                                skip: 0,
                                take: TAKE_ALL,
                                sortColumn: 'supplierLegalName',
                                sortDirection: SortDirectionFinancing.DESCENDING
                            }
                        })
                    );
            } else {
                suppliersResponse =
                    await this.listSuppliersAssociatedWithAccountPaymentService.executeOnce(
                        new AccountPaymentsDetailAssociatedSuppliersRequest({
                            batchKey: this.paymentKey,
                            accountPaymentsListPaymentTypeEnum: this.paymentTypeRequestString,
                            pagination: {
                                skip: 0,
                                take: TAKE_ALL,
                                sortColumn: 'supplierLegalName',
                                sortDirection: SortDirectionFinancing.DESCENDING
                            }
                        })
                    );
            }

            suppliersResponse?.rows?.forEach((supplier) => {
                this.supplierFilterOptions.push({
                    label: supplier.supplierLegalName!,
                    value: supplier.supplierKey
                });
            });
            this.supplierFilterOptions = uniqBy(this.supplierFilterOptions, 'value');

            const queryParamMap = this._activatedRoute.snapshot.queryParamMap;
            const invoiceNumber =
                queryParamMap.get(FastTrackRouteQueryParams.SearchText) ?? undefined;
            const sellerKeys =
                queryParamMap.getAll(FastTrackRouteQueryParams.SupplierKeysFilter) ?? [];
            if (invoiceNumber || sellerKeys) {
                this.paymentDetailFiltersFormGroup.patchValue(
                    {
                        search: invoiceNumber,
                        supplierFilter: sellerKeys
                    },
                    {
                        onlySelf: true,
                        emitEvent: false
                    }
                );
            }

            if (!this._featureFlagService.isOnSync(FeatureFlags.BuyerAccountPaymentsPage)) {
                this.dataSource = new LsqApiServiceListSource(
                    this.getPaymentService,
                    new BuyerPaymentsDetailRequest({
                        batchKey: this.paymentKey,
                        buyerPaymentsListPaymentTypeEnum: this.paymentTypeRequestString,
                        pagination: {
                            skip: 0,
                            take: 25,
                            sortColumn: 'invoiceDate',
                            sortDirection: SortDirectionFinancing.DESCENDING
                        }
                    })
                );
            } else {
                this.dataSource = new LsqApiServiceListSource(
                    this.getAccountPaymentService,
                    new AccountPaymentsDetailAssociatedSuppliersRequest({
                        batchKey: this.paymentKey,
                        accountPaymentsListPaymentTypeEnum: this.paymentTypeRequestString,
                        pagination: {
                            skip: 0,
                            take: 25,
                            sortColumn: 'invoiceDate',
                            sortDirection: SortDirectionFinancing.DESCENDING
                        }
                    })
                );
            }

            this.dataSource.data$.subscribe(() => {
                this.setFilterQueryParams();
            });

            this.dataSource.data$.pipe(forLifeOf(this)).subscribe((response) => {
                this.summaryDataSource = new LsqListSource<BuyerPaymentsSummaryItem>({
                    data$: of(response.buyerPaymentsSummaryItem),
                    rows$: of([response.buyerPaymentsSummaryItem!])
                });

                this.payerTypeDisplay.DEPOSIT_ACCOUNT = response.buyerLegalName || '';
                this.payerTypeDisplay.BUYER = response.buyerLegalName || '';

                this.isSummaryLoaded = true; // This never gets unset because the top should never change.

                this.paymentDetailFiltersFormGroup.valueChanges
                    .pipe(distinctUntilChanged())
                    .subscribe((filters) => {
                        this.expandedGroupInvoiceKeys.clear();
                        const oldRequest = this.dataSource.request;
                        this.dataSource.dispatchRequest(
                            new BuyerPaymentsDetailRequest({
                                ...oldRequest,
                                searchValue: filters.search || '',
                                supplierSearchList: filters.supplierFilter,
                                pagination: {
                                    ...oldRequest.pagination,
                                    skip: 0
                                }
                            })
                        );
                    });
            });

            this.reportName$ = this.dataSource.data$.pipe(
                map(
                    (data) =>
                        `Payment_Detail_${this.lsqDatePipe.transform(
                            parseYearMonthDayStringToDate(
                                data.buyerPaymentsSummaryItem?.paymentDate ?? ''
                            ),
                            DateFormat.LsqShortYearDashed
                        )}`
                )
            );

            this.paymentPayer$ = this.dataSource.data$.pipe(
                map((data) => data.buyerPaymentsSummaryItem?.payer ?? '')
            );
        });

        this.paymentDetailFiltersFormGroup.valueChanges
            .pipe(
                forLifeOf(this),
                switchMap(() =>
                    this.dataSource.rows$.pipe(
                        forLifeOf(this),
                        skip(1),
                        first(),
                        filter((invoiceRows) =>
                            invoiceRows.some((invoiceRow) => !!invoiceRow.groupedChildSearchMatch)
                        )
                    )
                )
            )
            .subscribe(async (invoiceRows) => {
                const filteredRows = invoiceRows.filter(
                    (invoiceRow) => invoiceRow.groupedChildSearchMatch
                );
                for (const row of filteredRows) {
                    await this._expandGroupByInvoiceKey(row.invoiceKey!);
                }
            });
    }

    public navigateBack(): void {
        this._location.back();
    }

    public async handleInvoiceGroupToggleClick(row: BuyerPaymentsDetailItem): Promise<void> {
        if (this.expandedGroupInvoiceKeys.has(row.invoiceKey!)) {
            await this._collapseGroup(row.invoiceKey!);
        } else {
            await this._expandGroup(row);
        }
    }

    private async _expandGroup(row: BuyerPaymentsDetailItem): Promise<void> {
        await this._expandGroupByInvoiceKey(row.invoiceKey!);
    }

    private async _collapseGroup(invoiceKey: string): Promise<void> {
        this.expandedGroupInvoiceKeys.delete(invoiceKey);
        await this.dataSource.collapseGroup('invoiceKey', invoiceKey);
    }

    private async _expandGroupByInvoiceKey(invoiceKey: string): Promise<void> {
        this.expandedGroupInvoiceKeys.add(invoiceKey);
        let invoiceSummaryRows = await this.dataSource.rows$.pipe(first()).toPromise();
        let index = invoiceSummaryRows.findIndex((row) => row.invoiceKey === invoiceKey);
        if (index < 0) {
            return;
        }

        const loadingRow: BuyerPaymentsDetailItem = {
            invoiceKey: invoiceKey,
            groupId: 'loading' // So we get the grouped invoice styling
        };

        (loadingRow as LoadableBuyerPaymentsDetailItem).isRowLoading = true;
        this.dataSource.expandGroup(index, [loadingRow]);

        let groupedInvoices = [];

        if (this._featureFlagService.isOnSync(FeatureFlags.BuyerAccountPaymentsPage)) {
            groupedInvoices =
                (
                    await this.getAccountGroupedPaymentService.executeOnce(
                        new AccountPaymentsGroupedDetailRequest({
                            batchKey: this.paymentKey,
                            invoiceKey,
                            paymentType: this.paymentTypeRequestString,
                            pagination: {
                                skip: 0,
                                take: TAKE_ALL,
                                sortColumn: 'invoiceDate',
                                sortDirection: SortDirectionFinancing.DESCENDING
                            }
                        })
                    )
                ).rows || [];
        } else {
            groupedInvoices =
                (
                    await this.getGroupedPaymentService.executeOnce(
                        new BuyerPaymentsGroupedDetailRequest({
                            batchKey: this.paymentKey,
                            invoiceKey,
                            paymentType: this.paymentTypeRequestString,
                            pagination: {
                                skip: 0,
                                take: TAKE_ALL,
                                sortColumn: 'invoiceDate',
                                sortDirection: SortDirectionFinancing.DESCENDING
                            }
                        })
                    )
                ).rows || [];
        }

        await this.dataSource.collapseGroup('invoiceKey', invoiceKey);
        invoiceSummaryRows = await this.dataSource.rows$.pipe(first()).toPromise();
        index = invoiceSummaryRows.findIndex((row) => row.invoiceKey === invoiceKey);

        if (index < 0) {
            return;
        }

        this.dataSource.expandGroup(index, groupedInvoices);
    }

    private _getRowClassName(row: BuyerPaymentsGroupedDetailItem): string {
        const classList = [];
        if (row.isGroup) {
            classList.push('is-group');

            if (this.expandedGroupInvoiceKeys.has(row.invoiceKey!)) {
                classList.push('expanded');
            }
        }
        if (row.groupedInvoiceKey) {
            classList.push('is-member-of-group');
        }
        return classList.join(' ');
    }

    public isInvoiceClickable(row: BuyerPaymentsGroupedDetailItem): boolean {
        return (
            (row.isGroup &&
                this._featureFlagService.isOnSync(FeatureFlags.PendingCreditMemosDetails)) ||
            this._featureFlagService.isOnSync(FeatureFlags.BuyerAccountInvoiceDetails)
        );
    }

    public async setFilterQueryParams(): Promise<void> {
        const queryParams = {
            ...TableHelper.getQueryParamsByListSource(this.dataSource),
            [FastTrackRouteQueryParams.SearchText]:
                this.paymentDetailFiltersFormGroup.controls.search.value || undefined,
            [FastTrackRouteQueryParams.SupplierKeysFilter]:
                this.paymentDetailFiltersFormGroup.controls.supplierFilter.value || undefined,
            tab: FastTrackRouteQueryParams.InvoicesTab
        };
        await this._router.navigate([], {
            relativeTo: this._activatedRoute,
            queryParams: queryParams,
            replaceUrl: true
        });
    }

    public async goToGroupDetail(invoice: BuyerPaymentsGroupedDetailItem): Promise<void> {
        const isGroup = invoice.isGroup;
        const groupedInvoiceDetailRoute = this.isFinancingAccountPortalUser
            ? SUPPLY_CHAIN_DIRECT_ACCOUNT_PARENT_GROUPED_INVOICE_DETAIL
            : SUPPLY_CHAIN_DIRECT_BUYER_PARENT_GROUPED_INVOICE_DETAIL;

        await this.paymentPayer$.subscribe((event) => (this.payer = event));
        void this._router.navigateByUrl(
            this._router.createUrlTree(
                getDynamicRouterLink(
                    getRouterLinkById(
                        atlas,
                        isGroup ? groupedInvoiceDetailRoute : BUYER_ACCOUNT_INVOICE_DETAIL,
                        {
                            noStandaloneLeadingSlash: true
                        }
                    ),
                    {
                        invoiceKey: invoice.invoiceKey
                    }
                ),
                {
                    queryParams: {
                        [FastTrackRouteQueryParams.PreviousRouteId]: BUYER_PAYMENT_DETAILS,
                        [FastTrackRouteQueryParams.IsPayerLSQ]: this.payer === 'LSQ'
                    }
                }
            )
        );
    }

    public goSupplierDetails(sellerKey: string) {
        this._router.navigateByUrl(
            this._router.createUrlTree(
                getDynamicRouterLink(
                    getRouterLinkById(
                        atlas,
                        this.isFinancingAccountPortalUser
                            ? ACCOUNT_SUPPLIER_DETAILS
                            : BUYER_SUPPLIER_DETAILS,
                        {
                            noStandaloneLeadingSlash: true
                        }
                    ),
                    { sellerKey }
                ),
                {
                    queryParams: {
                        [FastTrackRouteQueryParams.PreviousRouteId]: BUYER_PAYMENT_DETAILS
                    }
                }
            )
        );
    }

    public getPaymentType(row: BuyerPaymentsDetailItem2): string {
        if (row.paymentType === 'INVOICE_TERM' && row.payLaterDate) {
            return this.buyerPaymentsPaymentTypeDisplayEnum.PAY_LATER_PAYMENT;
        }
        return this.buyerPaymentsPaymentTypeDisplayEnum[
            row.paymentType as keyof typeof this.buyerPaymentsPaymentTypeDisplayEnum
        ];
    }
}
export interface LoadableBuyerPaymentsDetailItem extends BuyerPaymentsDetailItem {
    isRowLoading?: boolean;
}

export const BUYER_PAYMENT_DETAILS = 'BUYER_PAYMENT_DETAILS';
